/**
 * MeetingCard Component
 * Displays individual meeting information with actions
 */

import { format } from 'date-fns';
import { Calendar, Clock, Users, Video, Phone, MapPin, MoreVertical, Check, X, Edit, Trash2, RefreshCw, User, Building2 } from 'lucide-react';
import { Meeting, MeetingStatus } from '@/types/calendar.types';
import { MEETING_STATUS_LABELS, MEETING_FORMAT_LABELS, CONTACT_ROLE_LABELS } from '@/constants/calendar.constants';
import { useLanguage } from '@/contexts/LanguageContext';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { cn } from '@/lib/utils';

interface MeetingCardProps {
  meeting: Meeting;
  onConfirm?: (id: string) => void;
  onCancel?: (id: string) => void;
  onComplete?: (id: string) => void;
  onEdit?: (meeting: Meeting) => void;
  onDelete?: (id: string) => void;
  onJoin?: (meeting: Meeting) => void;
  onReschedule?: (meeting: Meeting) => void;
  onNavigate?: (meeting: Meeting) => void;
}

const statusColors: Record<MeetingStatus, string> = {
  pending: 'bg-yellow-500/10 text-yellow-500 border-yellow-500/20',
  confirmed: 'bg-green-500/10 text-green-500 border-green-500/20',
  cancelled: 'bg-red-500/10 text-red-500 border-red-500/20',
  completed: 'bg-gray-500/10 text-gray-500 border-gray-500/20',
  rescheduled: 'bg-blue-500/10 text-blue-500 border-blue-500/20',
};

export function MeetingCard({
  meeting,
  onConfirm,
  onCancel,
  onComplete,
  onEdit,
  onDelete,
  onJoin,
  onReschedule,
  onNavigate,
}: MeetingCardProps) {
  const { language } = useLanguage();
  const lang = language === 'fr' ? 'fr' : 'en';

  const meetingDate = new Date(meeting.date);
  const isToday = format(meetingDate, 'yyyy-MM-dd') === format(new Date(), 'yyyy-MM-dd');
  const isPast = meetingDate < new Date();
  const meetingFormat = meeting.format || 'video';
  const canJoin = meeting.status === 'confirmed' && meeting.meetingUrl && !isPast;

  const FormatIcon = meetingFormat === 'video' ? Video : meetingFormat === 'phone' ? Phone : MapPin;

  return (
    <Card 
      className={cn(
        'transition-all hover:shadow-md',
        meeting.status === 'cancelled' && 'opacity-60',
        onNavigate && (meetingFormat === 'video' || meetingFormat === 'phone') && 'cursor-pointer'
      )}
      onClick={() => {
        if (onNavigate && (meetingFormat === 'video' || meetingFormat === 'phone')) {
          onNavigate(meeting);
        }
      }}
    >
      <CardContent className="p-4">
        <div className="flex items-start justify-between gap-4">
          <div className="flex-1 min-w-0">
            {/* Title & Type */}
            <div className="flex items-center gap-2 mb-2">
              <h3 className="font-semibold text-foreground truncate">{meeting.title}</h3>
              <Badge variant="outline" className={statusColors[meeting.status]}>
                {MEETING_STATUS_LABELS[meeting.status][lang]}
              </Badge>
            </div>

            {/* Format */}
            <div className="flex items-center gap-1.5 text-sm text-muted-foreground mb-3">
              <FormatIcon className="w-4 h-4" />
              <span>{MEETING_FORMAT_LABELS[meetingFormat][lang]}</span>
            </div>

            {/* Date & Time */}
            <div className="flex flex-wrap items-center gap-4 text-sm text-muted-foreground">
              <div className="flex items-center gap-1.5">
                <Calendar className="w-4 h-4" />
                <span className={isToday ? 'text-primary font-medium' : ''}>
                  {isToday ? (lang === 'fr' ? "Aujourd'hui" : 'Today') : format(meetingDate, 'MMM d, yyyy')}
                </span>
              </div>
              <div className="flex items-center gap-1.5">
                <Clock className="w-4 h-4" />
                <span>{meeting.time} ({meeting.duration} min)</span>
              </div>
              <div className="flex items-center gap-1.5">
                <Users className="w-4 h-4" />
                <span>{meeting.participants.length} {lang === 'fr' ? 'participants' : 'attendees'}</span>
              </div>
            </div>

            {/* Contact info */}
            {meeting.contact && (
              <div className="flex flex-wrap items-center gap-4 text-sm text-muted-foreground mt-1">
                <div className="flex items-center gap-1.5">
                  <User className="w-4 h-4" />
                  <span className="font-medium">{meeting.contact.firstName} {meeting.contact.lastName}</span>
                  <span className="text-xs">({CONTACT_ROLE_LABELS[meeting.contact.role]?.[lang] || meeting.contact.role})</span>
                </div>
                {meeting.contact.company && (
                  <div className="flex items-center gap-1.5">
                    <Building2 className="w-4 h-4" />
                    <span>{meeting.contact.company}</span>
                  </div>
                )}
                {meeting.contact.phone && (
                  <div className="flex items-center gap-1.5">
                    <Phone className="w-4 h-4" />
                    <span>{meeting.contact.phone}</span>
                  </div>
                )}
              </div>
            )}

            {/* External address */}
            {meeting.format === 'in_person' && meeting.location === 'external' && meeting.externalAddress && (
              <div className="flex items-center gap-1.5 text-sm text-muted-foreground mt-1">
                <MapPin className="w-4 h-4" />
                <span>{meeting.externalAddress}</span>
              </div>
            )}

            {/* Description */}
            {meeting.description && (
              <p className="text-sm text-muted-foreground mt-2 line-clamp-2">
                {meeting.description}
              </p>
            )}
          </div>

          {/* Actions */}
          <div className="flex items-center gap-2">
            {canJoin && meetingFormat === 'video' && (
              <Button 
                size="sm" 
                onClick={() => onJoin?.(meeting)}
                className="gap-1"
              >
                <Video className="w-4 h-4" />
                {lang === 'fr' ? 'Rejoindre' : 'Join'}
              </Button>
            )}
            {canJoin && meetingFormat === 'phone' && (
              <Button 
                size="sm" 
                variant="outline"
                onClick={() => onJoin?.(meeting)}
                className="gap-1"
              >
                <Phone className="w-4 h-4" />
                {lang === 'fr' ? 'Appeler' : 'Call'}
              </Button>
            )}

            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="ghost" size="icon" className="h-8 w-8">
                  <MoreVertical className="w-4 h-4" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                {meeting.status === 'pending' && onConfirm && (
                  <DropdownMenuItem onClick={() => onConfirm(meeting.id)}>
                    <Check className="w-4 h-4 mr-2 text-green-500" />
                    {lang === 'fr' ? 'Confirmer' : 'Confirm'}
                  </DropdownMenuItem>
                )}
                {meeting.status === 'confirmed' && !isPast && onComplete && (
                  <DropdownMenuItem onClick={() => onComplete(meeting.id)}>
                    <Check className="w-4 h-4 mr-2 text-primary" />
                    {lang === 'fr' ? 'Marquer terminé' : 'Mark Complete'}
                  </DropdownMenuItem>
                )}
                {onEdit && meeting.status !== 'cancelled' && meeting.status !== 'completed' && (
                  <DropdownMenuItem onClick={() => onEdit(meeting)}>
                    <Edit className="w-4 h-4 mr-2" />
                    {lang === 'fr' ? 'Modifier' : 'Edit'}
                  </DropdownMenuItem>
                )}
                {onReschedule && meeting.status !== 'cancelled' && meeting.status !== 'completed' && (
                  <DropdownMenuItem onClick={() => onReschedule(meeting)}>
                    <RefreshCw className="w-4 h-4 mr-2 text-blue-500" />
                    {lang === 'fr' ? 'Reprogrammer' : 'Reschedule'}
                  </DropdownMenuItem>
                )}
                <DropdownMenuSeparator />
                {meeting.status !== 'cancelled' && meeting.status !== 'completed' && onCancel && (
                  <DropdownMenuItem 
                    onClick={() => onCancel(meeting.id)}
                    className="text-destructive"
                  >
                    <X className="w-4 h-4 mr-2" />
                    {lang === 'fr' ? 'Annuler' : 'Cancel'}
                  </DropdownMenuItem>
                )}
                {onDelete && (
                  <DropdownMenuItem 
                    onClick={() => onDelete(meeting.id)}
                    className="text-destructive"
                  >
                    <Trash2 className="w-4 h-4 mr-2" />
                    {lang === 'fr' ? 'Supprimer' : 'Delete'}
                  </DropdownMenuItem>
                )}
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}
